import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Chip,
  Button,
  Divider,
  Grid,
  Alert,
  TextField,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { PageLayout } from "../components/page-layout";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

/* =========================
   UTILS
========================= */
const YEARS_THRESHOLD = 4.99;

const calculateInterval = (fromDate, toDate) => {
  const diffDays = toDate.diff(fromDate, "day");
  const years = diffDays / 365.25;
  return years < YEARS_THRESHOLD ? "1d" : "1mo";
};

const formatDate = (date) => date.format("YYYY-MM-DD");

const percentColor = (value) =>
  value >= 0 ? "success.main" : "error.main";

/* =========================
   COMPONENT
========================= */
export default function PortfolioPage() {
  const { user } = useAuth0();

  const [portfolio, setPortfolio] = useState([]);
  const [newTicker, setNewTicker] = useState("");

  const [fromDate, setFromDate] = useState(dayjs().subtract(5, "year"));
  const [toDate, setToDate] = useState(dayjs());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);

  const storageKey = `portfolio_${user?.email}`;

  /* ---------------- LOAD PORTFOLIO ---------------- */
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      setPortfolio(JSON.parse(saved));
    }
  }, [storageKey]);

  /* ---------------- ADD STOCK ---------------- */
  const handleAddStock = () => {
    const ticker = newTicker.trim().toUpperCase();

    if (!ticker) return;

    if (portfolio.includes(ticker)) {
      setError(`${ticker} is already in your portfolio.`);
      return;
    }

    const updated = [...portfolio, ticker];

    setPortfolio(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    setNewTicker("");
    setError(null);
  };

  /* ---------------- ENTER KEY ---------------- */
  const handleTickerKeyDown = (event) => {
    if (event.key === "Enter") {
      handleAddStock();
    }
  };

  /* ---------------- REMOVE STOCK ---------------- */
  const handleRemove = (ticker) => {
    const updated = portfolio.filter((t) => t !== ticker);

    setPortfolio(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  /* ---------------- CALCULATE CAGR ---------------- */
  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    setSummary(null);
    setResults([]);

    try {
      const interval = calculateInterval(fromDate, toDate);

      const payload = {
        symbols: portfolio,
        buy_date: formatDate(fromDate),
        end_date: formatDate(toDate),
        interval,
        use_adjclose: true,
      };

      const response = await fetch(
        "https://b4usjefnn7.execute-api.us-east-1.amazonaws.com/Prod/cagr",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to calculate CAGR");
      }

      const data = await response.json();

      setSummary(data.summary || null);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          My Portfolio
        </Typography>

        {/* ================= ADD STOCK ================= */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Stock
          </Typography>

          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              label="Ticker"
              placeholder="e.g. CBA.AX or AAPL"
              value={newTicker}
              onChange={(e) => setNewTicker(e.target.value)}
              onKeyDown={handleTickerKeyDown}
            />

            <Button
              variant="contained"
              onClick={handleAddStock}
              disabled={!newTicker.trim()}
            >
              Add
            </Button>
          </Box>
        </Paper>

        {/* ================= PORTFOLIO ================= */}
        {portfolio.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You haven't added any stocks to your portfolio yet.
          </Typography>
        ) : (
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {portfolio.map((ticker) => (
                <Chip
                  key={ticker}
                  label={
                    <Box
                      component={RouterLink}
                      to={`/stock/${ticker}`}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      {ticker}
                    </Box>
                  }
                  clickable={false}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                  onDelete={() => handleRemove(ticker)}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* ================= INPUT ================= */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Portfolio CAGR
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              sx={{ mb: 2 }}
            >
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                sx={{ flex: 1 }}
              />

              <DatePicker
                label="To"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                sx={{ flex: 1 }}
              />
            </Box>
          </LocalizationProvider>

          <Button
            variant="contained"
            fullWidth
            onClick={handleCalculate}
            disabled={loading || portfolio.length === 0}
          >
            {loading ? "Calculating..." : "Calculate CAGR"}
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* ================= SUMMARY ================= */}
        {summary && (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  Symbols
                </Typography>

                <Typography variant="h6">
                  {summary.symbols_valid}/
                  {summary.symbols_requested}
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  Avg CAGR
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: percentColor(
                      summary.average_cagr_percent
                    ),
                  }}
                >
                  {summary.average_cagr_percent.toFixed(2)}%
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  Avg Total Return
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: percentColor(
                      summary.average_total_return
                    ),
                  }}
                >
                  {summary.average_total_return.toFixed(2)}%
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ================= RESULTS ================= */}
        {results.length > 0 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Results by Stock
            </Typography>

            {results.map((r) => {
              /* ---- ERROR PER STOCK ---- */
              if (r.error) {
                return (
                  <Box key={r.symbol}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                    >
                      {r.symbol}
                    </Typography>

                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {r.error}
                    </Alert>

                    <Divider sx={{ my: 2 }} />
                  </Box>
                );
              }

              /* ---- NORMAL RESULT ---- */
              return (
                <Box key={r.symbol}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                    >
                      {r.symbol}
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: percentColor(
                          r.cagr_annual_percent
                        ),
                      }}
                    >
                      {r.cagr_annual_percent.toFixed(2)}% CAGR
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total Return:{" "}
                    <span
                      style={{
                        color:
                          r.total_return_percent >= 0
                            ? "#66bb6a"
                            : "#ef5350",
                      }}
                    >
                      {r.total_return_percent.toFixed(2)}%
                    </span>{" "}
                    • Period: {r.years.toFixed(2)} years •
                    Interval: {r.interval}
                  </Typography>

                  <Divider sx={{ my: 2 }} />
                </Box>
              );
            })}
          </Paper>
        )}
      </Container>
    </PageLayout>
  );
}